import dayjs from "dayjs";
import i18n from "features/shared/i18n";
import { COLOR_PALETTE, generateColorVariations } from "features/user/constants";
import {
  UserActivityLog,
  UserActivityType,
  CourseClearActivityData,
  ProjectActivityData,
} from "features/player/types";

// ヘルパー関数
export const isValidBirthday = (birthday: string): boolean => {
  if (!birthday) return false;
  if (birthday === "0000-00-00" || birthday.startsWith("0000")) return false;
  const date = dayjs(birthday);
  if (!date.isValid()) return false;
  if (date.year() < 1920) return false;
  return true;
};

export const calculateAge = (birthday: string): number | null => {
  if (!isValidBirthday(birthday)) return null;
  const birth = dayjs(birthday);
  return dayjs().diff(birth, "year");
};

export const getGenderLabel = (gender: string): string => {
  switch (gender) {
    case "male":
      return i18n.t("MSG_GENDER_MALE_BY_PARENT");
    case "female":
      return i18n.t("MSG_GENDER_FEMALE_BY_PARENT");
    default:
      return i18n.t("MSG_GENDER_NONE");
  }
};

export const formatDate = (dateString: string): string => {
  if (!isValidBirthday(dateString)) return "-";
  return dayjs(dateString).format("YYYY-MM-DD");
};

// 活動時間のフォーマット
export const formatActivityTime = (dateString: string): string => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  if (!date.isValid()) return "";

  const now = dayjs();
  if (date.isSame(now, "day")) {
    return `${i18n.t("MSG_PARENT_PAGE_TODAY")} ${date.format("HH:mm")}`;
  }
  if (date.isSame(now.subtract(1, "day"), "day")) {
    return `${i18n.t("MSG_PARENT_PAGE_YESTERDAY")} ${date.format("HH:mm")}`;
  }
  return date.format(i18n.t("MSG_PARENT_PAGE_DATE_FORMAT"));
};

// 色のベースキーから翻訳キーへのマッピング（保護者ページ用）
export const COLOR_LABEL_MAP: Record<string, string> = {
  red: "MSG_COLOR_RED_BY_PARENT",
  blue: "MSG_COLOR_BLUE_BY_PARENT",
  yellow: "MSG_COLOR_YELLOW_BY_PARENT",
  green: "MSG_COLOR_GREEN_BY_PARENT",
  white: "MSG_COLOR_WHITE_BY_PARENT",
};

// HEX値からベース色名を取得
export const getBaseColorFromHex = (hex: string): string | null => {
  const upperHex = hex.toUpperCase();
  for (const palette of COLOR_PALETTE) {
    if (palette.key === upperHex || palette.hex.toUpperCase() === upperHex) {
      return palette.key;
    }
    const variations = generateColorVariations(palette.key);
    if (variations.map((v) => v.toUpperCase()).includes(upperHex)) {
      return palette.key;
    }
  }
  return null;
};

export const PROFILE_OPTION_LABELS: Record<string, string> = {
  // 得意な教科（保護者ページ用）
  japanese: "MSG_SUBJECT_JAPANESE_BY_PARENT",
  math: "MSG_SUBJECT_MATH_BY_PARENT",
  science: "MSG_SUBJECT_SCIENCE_BY_PARENT",
  social: "MSG_SUBJECT_SOCIAL_BY_PARENT",
  english: "MSG_SUBJECT_ENGLISH_BY_PARENT",
  art: "MSG_SUBJECT_ART_BY_PARENT",
  music: "MSG_SUBJECT_MUSIC_BY_PARENT",
  pe: "MSG_SUBJECT_PE_BY_PARENT",
  ethics: "MSG_SUBJECT_ETHICS_BY_PARENT",
  home_ec: "MSG_SUBJECT_HOME_EC_BY_PARENT",
  programming: "MSG_SUBJECT_PROGRAMMING_BY_PARENT",
  life: "MSG_SUBJECT_LIFE_BY_PARENT",
  integrated: "MSG_SUBJECT_INTEGRATED_BY_PARENT",
  special_activities: "MSG_SUBJECT_SPECIAL_ACTIVITIES_BY_PARENT",
};

// 外見フィールド用のマッピング
export const APPEARANCE_OPTION_LABELS: Record<string, Record<string, string>> = {
  hairstyle: {
    very_short: "MSG_HAIRSTYLE_VERY_SHORT_BY_PARENT",
    short: "MSG_HAIRSTYLE_SHORT_BY_PARENT",
    medium: "MSG_HAIRSTYLE_MEDIUM_BY_PARENT",
    slightly_long: "MSG_HAIRSTYLE_SLIGHTLY_LONG_BY_PARENT",
    long: "MSG_HAIRSTYLE_LONG_BY_PARENT",
  },
  body_type: {
    thin: "MSG_BODY_TYPE_THIN_BY_PARENT",
    slightly_thin: "MSG_BODY_TYPE_SLIGHTLY_THIN_BY_PARENT",
    medium: "MSG_BODY_TYPE_MEDIUM_BY_PARENT",
    slightly_chubby: "MSG_BODY_TYPE_SLIGHTLY_CHUBBY_BY_PARENT",
    chubby: "MSG_BODY_TYPE_CHUBBY_BY_PARENT",
  },
  face_shape: {
    round: "MSG_FACE_SHAPE_ROUND_BY_PARENT",
    oval: "MSG_FACE_SHAPE_OVAL_BY_PARENT",
    oblong: "MSG_FACE_SHAPE_OBLONG_BY_PARENT",
    square: "MSG_FACE_SHAPE_SQUARE_BY_PARENT",
    triangle: "MSG_FACE_SHAPE_TRIANGLE_BY_PARENT",
  },
};

// アクセサリー用のラベルマッピング
export const ACCESSORY_OPTION_LABELS: Record<string, string> = {
  none: "MSG_ACCESSORY_NONE_BY_PARENT",
  megane: "MSG_ACCESSORY_MEGANE_BY_PARENT",
  boushi: "MSG_ACCESSORY_BOUSHI_BY_PARENT",
  hairpin: "MSG_ACCESSORY_HAIRPIN_BY_PARENT",
  ribbon: "MSG_ACCESSORY_RIBBON_BY_PARENT",
  bandaid: "MSG_ACCESSORY_BANDAID_BY_PARENT",
};

// アクセサリー配列の翻訳
export const translateAccessoryValues = (values: string[] | null): string => {
  if (!values || values.length === 0) return i18n.t("MSG_PARENT_PAGE_NOT_SET");
  return values
    .map((key) => {
      const labelKey = ACCESSORY_OPTION_LABELS[key];
      return labelKey ? i18n.t(labelKey) : key;
    })
    .join("、");
};

// キーを翻訳されたラベルに変換（複数選択用）
export const translateProfileValues = (values: string[] | null): string => {
  if (!values || values.length === 0) return i18n.t("MSG_PARENT_PAGE_NOT_SET");
  return values
    .map((key) => {
      // まず得意な教科のラベルを確認
      const labelKey = PROFILE_OPTION_LABELS[key];
      if (labelKey) return i18n.t(labelKey);

      // HEX値の場合、ベース色名に変換して翻訳
      if (key.startsWith("#")) {
        const baseColor = getBaseColorFromHex(key);
        if (baseColor && COLOR_LABEL_MAP[baseColor]) {
          return i18n.t(COLOR_LABEL_MAP[baseColor]);
        }
      }

      // ベース色名の場合
      if (COLOR_LABEL_MAP[key]) {
        return i18n.t(COLOR_LABEL_MAP[key]);
      }

      return key;
    })
    .join(", ");
};

// 単一選択フィールドの翻訳（外見用）
export const translateSingleValue = (
  value: string | null,
  fieldType: "hairstyle" | "body_type" | "face_shape",
): string => {
  if (!value) return i18n.t("MSG_PARENT_PAGE_NOT_SET");
  const labels = APPEARANCE_OPTION_LABELS[fieldType];
  if (!labels) return value;
  const labelKey = labels[value];
  return labelKey ? i18n.t(labelKey) : value;
};

// 活動タイプに応じたアイコンクラス
export const getActivityIconClass = (type: string): string => {
  switch (type) {
    case UserActivityType.COURSE_LEVEL_CLEAR:
      return "bg-[rgba(255,191,0,0.15)] text-orange-100";
    case UserActivityType.PROJECT_CREATED:
      return "bg-[rgba(76,157,241,0.15)] text-blue-100";
    case UserActivityType.PROJECT_PUBLISHED:
      return "bg-[rgba(43,220,152,0.15)] text-green-100";
    case UserActivityType.PROJECT_FAVORITED:
      return "bg-[rgba(252,101,96,0.15)] text-pink-100";
    case UserActivityType.PROJECT_FAVORITED_RECEIVED:
      return "bg-[rgba(252,101,96,0.15)] text-danger";
    case UserActivityType.LOGIN:
      return "bg-[rgba(20,184,166,0.15)] text-teal-600";
    case UserActivityType.ICON_CHANGED:
      return "bg-[rgba(147,51,234,0.15)] text-purple-100";
    case UserActivityType.AI_CHAT_SESSION_STARTED:
      return "bg-[rgba(6,182,212,0.15)] text-cyan-100";
    case UserActivityType.INQUIRY_SENT:
      return "bg-green-50 text-green-700";
    case UserActivityType.APP_REVIEWED:
      return "bg-yellow-100 text-yellow-700";
    case UserActivityType.FEEDBACK_SENT:
      return "bg-blue-50 text-blue-700";
    default:
      return "bg-[rgba(76,157,241,0.15)] text-blue-100";
  }
};

// 活動タイプに応じたアイコン
export const getActivityIcon = (type: string): string => {
  switch (type) {
    case UserActivityType.COURSE_LEVEL_CLEAR:
      return "emoji_events";
    case UserActivityType.PROJECT_CREATED:
      return "palette";
    case UserActivityType.PROJECT_PUBLISHED:
      return "public";
    case UserActivityType.PROJECT_FAVORITED:
    case UserActivityType.PROJECT_FAVORITED_RECEIVED:
      return "favorite";
    case UserActivityType.LOGIN:
      return "login";
    case UserActivityType.ICON_CHANGED:
      return "face";
    case UserActivityType.AI_CHAT_SESSION_STARTED:
      return "smart_toy";
    case UserActivityType.INQUIRY_SENT:
      return "mail";
    case UserActivityType.APP_REVIEWED:
      return "star";
    case UserActivityType.FEEDBACK_SENT:
      return "feedback";
    default:
      return "info";
  }
};

// 活動タイトルの生成
export const getActivityTitle = (activity: UserActivityLog): string => {
  const data = activity.activity_data;

  switch (activity.activity_type) {
    case UserActivityType.COURSE_LEVEL_CLEAR: {
      const courseData = data as CourseClearActivityData;
      const courseNames: Record<string, string> = {
        algorithm: i18n.t("MSG_COURSE_ALGORITHM"),
        algorithm_tangible: i18n.t("MSG_COURSE_ALGORITHM"),
        program: i18n.t("MSG_COURSE_PROGRAM"),
        algorithm_advanced: i18n.t("MSG_COURSE_ALGORITHM_ADVANCED"),
        group_work: i18n.t("MSG_COURSE_GROUP_WORK"),
      };
      const courseName =
        courseNames[courseData?.course_type ?? ""] ?? courseData?.course_type ?? "";
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_COURSE_CLEAR", {
        course: courseName,
        stage: courseData?.stage_id ?? "",
        level: courseData?.level_id ?? "",
      });
    }
    case UserActivityType.PROJECT_CREATED: {
      const projectData = data as ProjectActivityData;
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_PROJECT_CREATED", {
        name: projectData?.project_name ?? "",
      });
    }
    case UserActivityType.PROJECT_PUBLISHED: {
      const projectData = data as ProjectActivityData;
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_PROJECT_PUBLISHED", {
        name: projectData?.project_name ?? "",
      });
    }
    case UserActivityType.PROJECT_FAVORITED: {
      const projectData = data as ProjectActivityData;
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_PROJECT_FAVORITED", {
        name: projectData?.project_name ?? "",
      });
    }
    case UserActivityType.PROJECT_FAVORITED_RECEIVED:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_FAVORITED_RECEIVED");
    case UserActivityType.LOGIN:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_LOGIN");
    case UserActivityType.ICON_CHANGED:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_ICON_CHANGED");
    case UserActivityType.AI_CHAT_SESSION_STARTED:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_AI_CHAT");
    case UserActivityType.INQUIRY_SENT:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_INQUIRY_SENT");
    case UserActivityType.APP_REVIEWED: {
      const reviewData = data as { rating?: number };
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_APP_REVIEWED", { rating: reviewData?.rating ?? 0 });
    }
    case UserActivityType.FEEDBACK_SENT:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_FEEDBACK_SENT");
    default:
      return i18n.t("MSG_PARENT_PAGE_ACTIVITY_DEFAULT");
  }
};

// プロフィールタブ定義
export enum ParentProfileTab {
  BASIC = "basic",
  APPEARANCE = "appearance",
  FAVORITES = "favorites",
}

export const PARENT_PROFILE_TAB_OPTIONS = [
  { key: ParentProfileTab.BASIC, labelKey: "MSG_PARENT_PAGE_TAB_BASIC" },
  { key: ParentProfileTab.APPEARANCE, labelKey: "MSG_PARENT_PAGE_TAB_APPEARANCE" },
  { key: ParentProfileTab.FAVORITES, labelKey: "MSG_PARENT_PAGE_TAB_FAVORITES" },
];
